// @react
import React from 'react'
// @types
import {
  FilterOrder,
  GetUserContentQuery,
  GetUserContentQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
  UserContentOrderByList,
  UserContentRelationship,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER_CONTENT } from 'graphql/queries/userContent.query'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import Helper from 'utils/Helper.class'
import strings from 'lib/constants/strings'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
// @components
import TextLink from '@bit/atd.web.text-link'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import QueryError from '../../../common/components/Errors/QueryError'
import { ExternalRouteNames } from '../../../../constants/routeNames'
import { ReactElement } from 'react'

/**
 * 2.0.0.10  MyATD - Dashboard - User Content
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3528%3A41884
 *
 *
 * endpoint: algolia/userContent
 * response: GeneralContentDTO
 * response namespaces:
 */

const useStyles = makeStyles(() => ({
  title: {
    textAlign: 'center',
  },
}))

const UserContent = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserContentQuery,
    GetUserContentQueryVariables
  >(GET_USER_CONTENT, {
    variables: {
      input: {
        page: 1,
        pageSize: 2,
        filters: {
          userRelationship: UserContentRelationship.AUTHORED,
        },
        orderBy: {
          order: FilterOrder.ASC,
          value: UserContentOrderByList.PUBLISHED_DATE,
        },
      },
    },
  })

  const { data: userData } = useQuery<GetUserQuery, GetUserQueryVariables>(
    GET_USER,
    { fetchPolicy: 'cache-first' },
  )

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  if (
    !userData?.user?.isContributor ||
    !userData?.user?.visibility.publicProfile
  ) {
    return <></>
  }

  const renderBody = (
    <Grid container>
      {data?.userContent?.data.length ? (
        data?.userContent?.data?.map((item, index) => {
          return (
            <React.Fragment key={`${item.id}_${index}`}>
              {index > 0 && <Spacer size={12} />}
              <Grid key={`${item.__typename}_${item.id}`} xs={12} item>
                <TextLink href={item?.url}>{item?.title}</TextLink>
                <div>{Helper.miniDateFormatter(item?.publishDate)}</div>
              </Grid>
            </React.Fragment>
          )
        })
      ) : (
        <>
          <Grid item xs={12}>
            <Typography className={classes.title} component={'h3'}>
              Write a blog post or start a community discussion.
            </Typography>
          </Grid>

          <Spacer size={16} />

          <Grid item xs={12}>
            <Button
              fullWidth
              alignment={'center'}
              variant="secondary"
              href={ExternalRouteNames.ATD_CONTRIBUTE}
            >
              Contribute to ATD
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  )

  const content = !isLoading ? (
    <Box
      variant="subModule"
      header={
        data?.userContent?.data.length
          ? {
              title: 'My Contributions',
            }
          : undefined
      }
      footer={
        data?.userContent?.data.length
          ? {
              link: {
                url: Helper.getPathFromRouteKey(
                  'MYATD_ACTIVITYHISTORY_USERCONTENT',
                ),
                label: strings.DASHBOARD.SEE_ALL_CONTRIBUTIONS,
              },
            }
          : undefined
      }
    >
      {renderBody}
    </Box>
  ) : (
    <Box
      variant="subModule"
      header={{
        title: 'My Contributions',
      }}
    >
      <SkeletonLoader />
    </Box>
  )

  return content
}

export default UserContent
